<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { useI18n } from "vue-i18n"

// ================================
// PROPS & EMITS
// ================================
const { t } = useI18n({
    messages: {
        fr: {
            alarm: {
                periodStart: "Début de la période",
                periodEnd: "Fin de la période",
                labelConsigneNight: "Consigne (nuit)",
                labelLimHauteNight: "Limite haute (nuit)",
                labelLimBasseNight: "Limite basse (nuit)",
            },
        },
    },
})

type NightTripletErrors = {
    consigne?: string
    limhaute?: string
    limbasse?: string
}

const props = defineProps<{
    /** Titre de la carte (ex. « Période de nuit (semaine) »). */
    sectionTitle: string
    /** Classe Tailwind du titre de section (défaut : `text-base`). */
    sectionTitleClass?: string
    disabled?: boolean
    inputIdStartNight: string
    inputIdEndNight: string
    inputIdNightConsigne: string
    inputIdNightLimhaute: string
    inputIdNightLimbasse: string
    startNightOptions: { label: string; value: number }[]
    endNightOptions: { label: string; value: number }[]
    /** Erreurs vee-validate sur le triplet nuit. */
    nightThresholdErrors?: NightTripletErrors
    /** Unité métier (`ConfigVariable.unit`) pour les champs seuil. */
    variableUnit?: string
}>()

const startNight = defineModel<number>("startNight", { required: true })
const endNight = defineModel<number>("endNight", { required: true })
const nightConsigne = defineModel<number>("nightConsigne", { required: true })
const nightLimhaute = defineModel<number>("nightLimhaute", { required: true })
const nightLimbasse = defineModel<number>("nightLimbasse", { required: true })

const emit = defineEmits<{
    /** Émis quand les seuils de nuit (triplet) changent. */
    change: []
}>()

// ================================
// INTERACTIONS
// ================================
/**
 * Met à jour l’heure de début de nuit.
 *
 * @param val valeur du select
 */
function onStartNightUpdate(val: unknown) {
    startNight.value = Number(val ?? 0)
    emit("change")
}

/**
 * Met à jour l’heure de fin de nuit.
 *
 * @param val valeur du select
 */
function onEndNightUpdate(val: unknown) {
    endNight.value = Number(val ?? 0)
    emit("change")
}

/**
 * Propage une modification du triplet nuit vers le parent.
 */
function onNightTripletChange() {
    emit("change")
}
</script>

<template>
    <div
        class="mt-4 flex flex-col gap-4 rounded-lg border border-slate-200/80 bg-slate-50/50 p-4"
    >
        <p
            class="font-medium text-primary"
            :class="props.sectionTitleClass ?? 'text-base'"
        >
            {{ sectionTitle }}
        </p>
        <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <BaseInputWrapper
                :label="t('alarm.periodStart')"
                :input-id="props.inputIdStartNight"
            >
                <BaseSelect
                    :model-value="startNight"
                    :input-id="props.inputIdStartNight"
                    :options="props.startNightOptions"
                    option-label="label"
                    option-value="value"
                    :disabled="props.disabled"
                    @update:model-value="onStartNightUpdate"
                />
            </BaseInputWrapper>
            <BaseInputWrapper
                :label="t('alarm.periodEnd')"
                :input-id="props.inputIdEndNight"
            >
                <BaseSelect
                    :model-value="endNight"
                    :input-id="props.inputIdEndNight"
                    :options="props.endNightOptions"
                    option-label="label"
                    option-value="value"
                    :disabled="props.disabled"
                    @update:model-value="onEndNightUpdate"
                />
            </BaseInputWrapper>
        </div>
        <div class="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <AlarmFormTripleFields
                v-model:consigne="nightConsigne"
                v-model:limhaute="nightLimhaute"
                v-model:limbasse="nightLimbasse"
                :variable-unit="props.variableUnit"
                :error-consigne="props.nightThresholdErrors?.consigne"
                :error-limhaute="props.nightThresholdErrors?.limhaute"
                :error-limbasse="props.nightThresholdErrors?.limbasse"
                :disabled="props.disabled"
                :input-id-consigne="props.inputIdNightConsigne"
                :input-id-limhaute="props.inputIdNightLimhaute"
                :input-id-limbasse="props.inputIdNightLimbasse"
                :label-consigne="t('alarm.labelConsigneNight')"
                :label-limhaute="t('alarm.labelLimHauteNight')"
                :label-limbasse="t('alarm.labelLimBasseNight')"
                @change="onNightTripletChange"
            />
        </div>
    </div>
</template>
